import React, { useState, useMemo, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { FiRefreshCw, FiUsers, FiClock, FiTrendingUp, FiTarget, FiCalendar, FiDownload } from 'react-icons/fi';
import { Link } from 'react-router-dom';
import Logo from '../common/Logo';
import { getCaliperMetrics, UserMetrics, CaliperMetricsResponse } from '../../api/admin';
import { TRACK_NAMES } from '../../types/constants';
import { includesAugust2025 } from '../../utils/dateRanges';
import { convertToCSV, downloadCSV, formatDateForFilename } from '../../utils/exportUtils';
import { UserCaliperDetails } from './UserCaliperDetails';

// Helper function to convert Date to local YYYY-MM-DD string
const formatDateLocal = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};


// Access denied component for non-admin users
const AccessDenied = () => (
  <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white flex flex-col justify-center p-6">
    <div className="max-w-4xl mx-auto text-center">
      <h1 className="text-2xl font-bold text-red-600">Access Denied</h1>
      <p className="text-base text-gray-600 mt-2">You are not authorized to view this page.</p>
    </div>
  </div>
);

// Header component
const CaliperMetricsHeader = () => (
  <h1 className="text-3xl font-bold text-center pt-4 mb-4 flex items-center justify-center gap-3">
    <Logo size={28} />
    <span className="bg-gradient-to-r from-blue-600 to-purple-600 text-transparent bg-clip-text">
      Caliper Metrics Dashboard
    </span>
  </h1>
);

// Date range selector component
interface DateRangeSelectorProps {
  startDate: string;
  endDate: string;
  onStartDateChange: (date: string) => void;
  onEndDateChange: (date: string) => void;
  onSearch: () => void;
  onRefresh: () => void;
  onDownloadCSV: () => void;
  isLoading?: boolean;
  hasData?: boolean;
}

const DateRangeSelector: React.FC<DateRangeSelectorProps> = ({
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange,
  onSearch,
  onRefresh,
  onDownloadCSV,
  isLoading = false,
  hasData = false
}) => {
  return (
    <Card className="shadow-lg border border-gray-100 mb-4">
      <CardContent className="p-4">
        <div className="flex flex-col sm:flex-row gap-4 items-end">
          <div className="flex-1">
            <label htmlFor="startDate" className="block text-sm font-medium text-gray-700 mb-2">
              Start Date
            </label>
            <input
              type="date"
              id="startDate"
              value={startDate}
              onChange={(e) => onStartDateChange(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              disabled={isLoading}
            />
          </div>
          <div className="flex-1">
            <label htmlFor="endDate" className="block text-sm font-medium text-gray-700 mb-2">
              End Date
            </label>
            <input
              type="date"
              id="endDate"
              value={endDate}
              onChange={(e) => onEndDateChange(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              disabled={isLoading}
            />
          </div>
          <div className="flex gap-2">
            <Button
              onClick={onSearch}
              disabled={isLoading || !startDate || !endDate}
              className="bg-blue-600 hover:bg-blue-700 text-white px-6"
            >
              <FiCalendar className="w-4 h-4 mr-2" />
              Search
            </Button>
            <Button
              onClick={onRefresh}
              disabled={isLoading || !startDate || !endDate}
              variant="outline"
              className="flex items-center px-4"
            >
              <FiRefreshCw className={`w-4 h-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Button
              onClick={onDownloadCSV}
              disabled={isLoading || !hasData}
              variant="outline"
              className="flex items-center px-4 bg-green-50 hover:bg-green-100 text-green-700 border-green-200"
            >
              <FiDownload className="w-4 h-4 mr-2" />
              CSV
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// Summary stats component
interface SummaryStatsProps {
  totalUsers: number;
  avgActiveTime: number;
  avgXp: number;
  avgAccuracy: number;
  isLoading?: boolean;
}

const SummaryStats: React.FC<SummaryStatsProps> = ({
  totalUsers,
  avgActiveTime,
  avgXp,
  avgAccuracy,
  isLoading = false
}) => {
  if (isLoading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {[...Array(4)].map((_, i) => (
          <Card key={i} className="shadow-lg border border-gray-100">
            <CardContent className="p-4">
              <div className="animate-pulse">
                <div className="h-4 bg-gray-300 rounded mb-2"></div>
                <div className="h-8 bg-gray-300 rounded mb-2"></div>
                <div className="h-3 bg-gray-300 rounded w-2/3"></div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  const stats = [
    {
      title: 'Total Users',
      value: totalUsers.toLocaleString(),
      icon: FiUsers,
      color: 'text-blue-600',
      bgColor: 'bg-blue-100'
    },
    {
      title: 'Avg Active Time',
      value: `${Math.round(avgActiveTime / 60)} min`,
      icon: FiClock,
      color: 'text-green-600',
      bgColor: 'bg-green-100'
    },
    {
      title: 'Avg XP',
      value: avgXp.toFixed(1),
      icon: FiTrendingUp,
      color: 'text-purple-600',
      bgColor: 'bg-purple-100'
    },
    {
      title: 'Avg Accuracy',
      value: `${avgAccuracy.toFixed(1)}%`,
      icon: FiTarget,
      color: 'text-orange-600',
      bgColor: 'bg-orange-100'
    }
  ];

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
      {stats.map((stat, index) => (
        <Card key={index} className="shadow-lg border border-gray-100 hover:shadow-xl transition-shadow duration-300">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">{stat.title}</p>
                <p className={`text-2xl font-bold ${stat.color}`}>
                  {stat.value}
                </p>
              </div>
              <div className={`p-2 rounded-lg ${stat.bgColor}`}>
                <stat.icon className={`w-5 h-5 ${stat.color}`} />
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

// User metrics table component
interface UserMetricsTableProps {
  users: UserMetrics[];
  dateRange: { startDate: string; endDate: string };
  isLoading?: boolean;
  onUserClick: (userEmail: string) => void;
}

const UserMetricsTable: React.FC<UserMetricsTableProps> = ({
  users,
  dateRange,
  isLoading = false,
  onUserClick
}) => {
  const showTrackColumn = !includesAugust2025(dateRange.startDate, dateRange.endDate);
  if (isLoading) {
    return (
      <Card className="shadow-lg border border-gray-100">
        <CardContent className="p-6">
          <div className="animate-pulse space-y-4">
            <div className="h-6 bg-gray-300 rounded w-1/3 mb-6"></div>
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-12 bg-gray-300 rounded"></div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (users.length === 0) {
    return (
      <Card className="shadow-lg border border-gray-100">
        <CardContent className="p-6 text-center">
          <div className="text-gray-400 mb-4">📊</div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No Data Found</h3>
          <p className="text-gray-600">
            No user activity found for the selected date range ({dateRange.startDate} to {dateRange.endDate}).
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="shadow-lg border border-gray-100">
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  User Email
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Active Time
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Waste Time
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Total XP
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  XP/Day
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Correct/Total Attempts
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Accuracy
                </th>
                {showTrackColumn && (
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Track Name
                  </th>
                )}
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Mastered Units
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {users.map((user, index) => (
                <tr key={user.userEmail} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                  <td className="px-4 py-3 whitespace-nowrap text-sm font-medium">
                    <button
                      onClick={() => onUserClick(user.userEmail)}
                      className="text-blue-600 hover:text-blue-800 hover:underline cursor-pointer"
                    >
                      {user.userEmail}
                    </button>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                    {Math.floor(user.activeTime / 60)}m {user.activeTime % 60}s
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                    {Math.floor(user.wasteTime / 60)}m {user.wasteTime % 60}s
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm font-semibold text-blue-600">
                    {user.xpTotal.toFixed(2)}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                    {user.averageXpPerDay.toFixed(2)}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                    {user.correctQuestions}/{user.totalQuestions}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      user.averageAccuracy >= 90 
                        ? 'bg-green-100 text-green-800' 
                        : user.averageAccuracy >= 75 
                        ? 'bg-yellow-100 text-yellow-800' 
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {user.averageAccuracy.toFixed(1)}%
                    </span>
                  </td>
                  {showTrackColumn && (
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                      {user.trackId ? (TRACK_NAMES[user.trackId as keyof typeof TRACK_NAMES] || user.trackId) : 'Multiple'}
                    </td>
                  )}
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                    {user.masteredUnits}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
};

// Error component
interface ErrorDisplayProps {
  error: string;
  onRetry: () => void;
}

const ErrorDisplay: React.FC<ErrorDisplayProps> = ({ error, onRetry }) => (
  <Card className="shadow-lg border border-red-100">
    <CardContent className="p-6 text-center">
      <div className="text-red-400 mb-4">⚠️</div>
      <h3 className="text-lg font-semibold text-gray-900 mb-2">Error Loading Data</h3>
      <p className="text-gray-600 mb-4">{error}</p>
      <Button onClick={onRetry} className="bg-red-600 hover:bg-red-700 text-white">
        <FiRefreshCw className="w-4 h-4 mr-2" />
        Try Again
      </Button>
    </CardContent>
  </Card>
);

// Main component
export const CaliperMetrics: React.FC = () => {
  const { user: currentUser } = useAuth();
  
  // Check if user is admin
  const isAdmin = useMemo(() =>
    currentUser?.role === 'admin',
    [currentUser?.role]
  );

  // State
  const [startDate, setStartDate] = useState(() => {
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - 2);
    return formatDateLocal(startDate);
  });
  const [endDate, setEndDate] = useState(() => formatDateLocal(new Date()));
  const [data, setData] = useState<CaliperMetricsResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedUserEmail, setSelectedUserEmail] = useState<string | null>(null);

  // Calculate summary stats from data
  const summaryStats = useMemo(() => {
    if (!data || !data.users || data.users.length === 0) {
      return { totalUsers: 0, avgActiveTime: 0, avgXp: 0, avgAccuracy: 0 };
    }

    const totalActiveTime = data.users.reduce((sum, user) => sum + user.activeTime, 0);
    const totalXp = data.users.reduce((sum, user) => sum + user.xpTotal, 0);
    const avgAccuracy = data.users.reduce((sum, user) => sum + user.averageAccuracy, 0) / data.users.length;
    const avgActiveTime = totalActiveTime / data.users.length;
    const avgXp = totalXp / data.users.length;

    return {
      totalUsers: data.users.length,
      avgActiveTime,
      avgXp,
      avgAccuracy
    };
  }, [data]);

  // Fetch data function
  const fetchData = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const result = await getCaliperMetrics(startDate, endDate);
      setData(result);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch Caliper metrics';
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [startDate, endDate]);

  // Retry function
  const handleRetry = useCallback(() => {
    fetchData();
  }, [fetchData]);

  // User click handler
  const handleUserClick = useCallback((userEmail: string) => {
    setSelectedUserEmail(userEmail);
  }, []);

  // Close modal handler
  const handleCloseModal = useCallback(() => {
    setSelectedUserEmail(null);
  }, []);

  // CSV download function
  const handleDownloadCSV = useCallback(() => {
    if (!data?.users || data.users.length === 0) return;

    const showTrackColumn = !includesAugust2025(startDate, endDate);
    
    // Define CSV headers based on table columns
    const headers = [
      { key: 'userEmail' as keyof UserMetrics, label: 'User Email' },
      { key: 'activeTime' as keyof UserMetrics, label: 'Active Time (seconds)' },
      { key: 'wasteTime' as keyof UserMetrics, label: 'Waste Time (seconds)' },
      { key: 'xpTotal' as keyof UserMetrics, label: 'Total XP' },
      { key: 'averageXpPerDay' as keyof UserMetrics, label: 'XP per Day' },
      { key: 'correctQuestions' as keyof UserMetrics, label: 'Correct Questions' },
      { key: 'totalQuestions' as keyof UserMetrics, label: 'Total Questions' },
      { key: 'averageAccuracy' as keyof UserMetrics, label: 'Accuracy (%)' },
      ...(showTrackColumn ? [{ key: 'trackId' as keyof UserMetrics, label: 'Track Name' }] : []),
      { key: 'masteredUnits' as keyof UserMetrics, label: 'Mastered Units' }
    ];

    // Transform data for CSV with formatted track names
    const csvData = data.users.map(user => ({
      ...user,
      trackId: user.trackId ? (TRACK_NAMES[user.trackId as keyof typeof TRACK_NAMES] || user.trackId) : 'Multiple'
    }));

    const csvContent = convertToCSV(csvData, headers);
    const filename = `caliper-metrics-${startDate}-to-${endDate}-${formatDateForFilename()}.csv`;
    
    downloadCSV(csvContent, filename);
  }, [data, startDate, endDate]);

  // Early return if not admin
  if (!isAdmin) {
    return <AccessDenied />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white p-4">
      <div className="max-w-7xl mx-auto space-y-4">
        <CaliperMetricsHeader />
        
        {/* Navigation */}
        <div className="flex mb-4">
          <Link 
            to="/admin" 
            className="text-blue-600 hover:text-blue-800 transition-colors duration-300"
          >
            ← Back to Admin Dashboard
          </Link>
        </div>

        {/* Date Range Selector */}
        <DateRangeSelector
          startDate={startDate}
          endDate={endDate}
          onStartDateChange={setStartDate}
          onEndDateChange={setEndDate}
          onSearch={fetchData}
          onRefresh={fetchData}
          onDownloadCSV={handleDownloadCSV}
          isLoading={isLoading}
          hasData={!!(data?.users && data.users.length > 0)}
        />

        {/* Error Display */}
        {error && (
          <ErrorDisplay error={error} onRetry={handleRetry} />
        )}

        {/* Dashboard Content */}
        {!error && (
          <>
            {/* Summary Statistics */}
            <SummaryStats
              totalUsers={summaryStats.totalUsers}
              avgActiveTime={summaryStats.avgActiveTime}
              avgXp={summaryStats.avgXp}
              avgAccuracy={summaryStats.avgAccuracy}
              isLoading={isLoading}
            />

            {/* User Metrics Table */}
            <UserMetricsTable
              users={data?.users || []}
              dateRange={data?.dateRange || { startDate, endDate }}
              isLoading={isLoading}
              onUserClick={handleUserClick}
            />
          </>
        )}

        {/* User Details Modal */}
        {selectedUserEmail && (
          <UserCaliperDetails
            userEmail={selectedUserEmail}
            startDate={startDate}
            endDate={endDate}
            onClose={handleCloseModal}
          />
        )}
      </div>
    </div>
  );
};